import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n, toEnPath, toEsPath } from '@/i18n/LanguageContext';
import {
  BRAND, brandDescription, postalAddressSchema, LOGO_IMAGE, SOCIAL_IMAGE, SOCIAL_IMAGE_SIZE,
} from '@/data/constants';


interface SeoProps {
  title: string;
  description: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  /** Page-specific share image. Falls back to the brand card in constants. */
  image?: string;
}

/**
 * ALIAS ROUTES -> CANONICAL DESTINATION.
 *
 * Three routes in src/App.tsx render an existing page under a second, legacy
 * URL:
 *
 *   /marketplace -> <Jobs />           (canonical /jobs   · /es/trabajos)
 *   /blog        -> <ResourcesHub />   (canonical /resources · /es/recursos)
 *   /reviews     -> <Testimonials />   (canonical /testimonials · /es/testimonios)
 *
 * DEFECT THIS FIXES: the canonical URL below is derived from the CURRENT
 * pathname, so each alias emitted a self-referencing canonical — telling search
 * engines that /en/marketplace and /en/jobs are two separate, equally valid
 * pages with byte-identical content. That splits link signals between duplicate
 * URLs and lets the wrong one win the query.
 *
 * The keys are LANGUAGE-NEUTRAL English path segments (the value produced by
 * toEnPath), so /es/marketplace resolves through the same entry and canonicals
 * to the Spanish primary, /es/trabajos — never across languages.
 *
 * The alias routes themselves are untouched: they still render, still work for
 * users and still honour every existing inbound link. No redirect is added —
 * a redirect would break any bookmark or external link mid-session for zero
 * additional indexing benefit, since rel=canonical already consolidates them.
 */
const ALIAS_CANONICALS: Record<string, string> = {
  '/marketplace': '/jobs',
  '/blog': '/resources',
  '/reviews': '/testimonials',
};



const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel: string, href: string, hreflang?: string) => {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo: React.FC<SeoProps> = ({ title, description, schema, noindex, image }) => {
  const { lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const full = `${title} | ${BRAND.fullName}`;
    document.title = full;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
    setMeta('property', 'og:title', full);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', BRAND.fullName);
    setMeta('name', 'twitter:card', 'summary_large_image');

    const origin = window.location.origin;
    const bare = location.pathname.replace(/^\/(en|es)(?=\/|$)/, '') || '/';

    /**
     * Resolve the language-neutral path FIRST, then fold any alias onto its
     * primary destination. Everything downstream — canonical, both hreflang
     * alternates, x-default, og:url and the WebPage @id — is built from this
     * single value, so an alias can never disagree with its primary.
     */
    const rawEnPath = toEnPath(bare);
    const enPath = ALIAS_CANONICALS[rawEnPath] || rawEnPath;
    const esPath = toEsPath(enPath);
    const enUrl = `${origin}/en${enPath === '/' ? '' : enPath}`;
    const esUrl = `${origin}/es${esPath === '/' ? '' : esPath}`;
    const canonical = lang === 'es' ? esUrl : enUrl;

    setLink('canonical', canonical);
    setLink('alternate', enUrl, 'en');
    setLink('alternate', esUrl, 'es');
    setLink('alternate', enUrl, 'x-default');


    /**
     * SHARE + LOCALE TAGS.
     *
     * DEFECT THIS FIXES: og:url, og:image and the twitter:* text tags were
     * never emitted. A `summary_large_image` card with no image renders as a
     * blank grey box in Slack/LinkedIn/X/iMessage, and with no og:url a
     * scraper has to guess the canonical URL of an SPA route. og:locale also
     * stayed pinned to the static en_US value from index.html even on /es
     * pages, which mislabels every Spanish page.
     */
    const share = image || SOCIAL_IMAGE;
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', share);
    setMeta('property', 'og:image:alt', BRAND.fullName);
    setMeta('property', 'og:image:width', String(SOCIAL_IMAGE_SIZE.width));
    setMeta('property', 'og:image:height', String(SOCIAL_IMAGE_SIZE.height));
    setMeta('property', 'og:locale', lang === 'es' ? 'es_US' : 'en_US');
    setMeta('property', 'og:locale:alternate', lang === 'es' ? 'en_US' : 'es_US');
    setMeta('name', 'twitter:title', full);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', share);

    // Structured data
    document.querySelectorAll('script[data-seo-schema]').forEach((n) => n.remove());
    const graph: Record<string, unknown>[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: BRAND.fullName,
        legalName: BRAND.fullName,
        /**
         * The wordmark on its own. Search engines routinely display the short
         * brand token as the site name; declaring it stops "GIGGONE" and
         * "GIGGONE Marketplace" being treated as two unrelated entities.
         */
        alternateName: BRAND.name,
        url: origin,
        email: BRAND.email,
        // Official company description — single source of truth in constants.
        description: brandDescription(lang),
        // No public telephone is published — email is the only contact channel.

        /**
         * CRAWLABLE LOGO. Required by Google's logo guidelines for the brand
         * knowledge entity; previously the Organization node had no image at
         * all, so nothing tied the company to a mark.
         */
        logo: {
          '@type': 'ImageObject',
          url: LOGO_IMAGE,
          caption: BRAND.fullName,
        },
        image: SOCIAL_IMAGE,

        // Verified street address — machine-readable for search engines, AI
        // crawlers, security reputation vendors and enterprise IT reviewers.
        address: postalAddressSchema(),
        areaServed: 'US',
        availableLanguage: ['en', 'es'],
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: BRAND.email,
            areaServed: 'US',
            availableLanguage: ['en', 'es'],
          },
          {
            '@type': 'ContactPoint',
            contactType: 'security',
            email: BRAND.email,
            availableLanguage: ['en', 'es'],
          },
        ],
      },
      {
        /**
         * WEBSITE ENTITY — one per page, with a stable @id so every WebPage
         * node below can point at it. This is the ONLY WebSite node the site
         * emits, and therefore the only place a SearchAction may be declared.
         *
         * SEARCHACTION IS NOW TRUTHFUL. It was removed earlier because the
         * browse page ignored `?q=` and only read trade / state / type /
         * budget / city — advertising a sitebox that did nothing was a false
         * structured-data claim. src/pages/Jobs.tsx now reads `q` from the
         * URL, renders a real keyword search input, filters the public feed on
         * it (title, description, trade, city, state, project type, budget
         * range) and keeps the term in the URL, so the target below describes
         * behaviour that genuinely exists and is reproducible by pasting the
         * URL.
         *
         * The target is the LOCALIZED browse path for the current language
         * (/en/jobs?q= or /es/trabajos?q=), derived from the same toEsPath
         * mapping the router uses — never a hand-typed string that could drift
         * from the real route.
         */
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        name: BRAND.fullName,
        alternateName: BRAND.name,
        url: origin,
        inLanguage: lang,
        description: brandDescription(lang),
        publisher: { '@id': `${origin}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/${lang}${lang === 'es' ? toEsPath('/jobs') : '/jobs'}?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },

      {
        /**
         * WEBPAGE ENTITY for the page actually being viewed. Gives every
         * public URL page-specific structured data that matches its visible
         * title/description, and ties it back to the site + organisation.
         */
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: full,
        description,
        inLanguage: lang,
        isPartOf: { '@id': `${origin}/#website` },
        about: { '@id': `${origin}/#organization` },
        primaryImageOfPage: { '@type': 'ImageObject', url: share },
      },
      {
        // LocalBusiness node — reputation systems and local search resolve the
        // company to a real, physical US address from this.
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        '@id': `${origin}/#localbusiness`,
        name: BRAND.fullName,
        url: origin,
        email: BRAND.email,
        description: brandDescription(lang),
        address: postalAddressSchema(),
        areaServed: { '@type': 'State', name: 'California' },
        parentOrganization: { '@id': `${origin}/#organization` },
      },
    ];


    if (schema) {
      (Array.isArray(schema) ? schema : [schema]).forEach((s) => graph.push({ '@context': 'https://schema.org', ...s }));
    }
    graph.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    // Track AI / assistant referrals (ChatGPT, Copilot, Gemini, Perplexity)
    try {
      const ref = document.referrer.toLowerCase();
      const sources = ['chatgpt', 'openai', 'copilot', 'bing', 'gemini', 'google.com/search?udm', 'perplexity'];
      const match = sources.find((s) => ref.includes(s));
      if (match) sessionStorage.setItem('ai_referral', match);
    } catch (_e) {
      /* ignore */
    }
  }, [title, description, schema, noindex, image, lang, location.pathname]);

  return null;
};

export default Seo;
